/**
 * Validate Date - Date, time, and month/year validation functions.
 * Provides validators for date fields with min/max constraints.
 */

// Declare external dependencies

// Types
interface ValidatorElement {
  converter?: (value: string) => number;
  partition?: number;
  monthFormat?: number;
  yearFormat?: number;
  minimum?: any;
  maximum?: any;
  seconds?: boolean;
  military?: boolean;
  get: () => string;
  set: (value: string) => void;
  setMessage: (message: string) => boolean;
  compareNullable: (value: any, operator: string) => boolean;
}

interface MonthYearInstance {
  month: number;
  year: number | null;
  day: number;
  checkIsValid: () => string;
  setFromString: (str: string, partition?: number) => MonthYearInstance;
  setFromInt: (value: number) => MonthYearInstance;
  setFromDate: (date: Date) => MonthYearInstance;
  getAsString: (monthFormat?: number, yearFormat?: number) => string;
  getAsInt: () => number;
  getAsDate: () => Date;
  getMonthName: (short?: boolean) => string;
  getDaysInMonth: () => number;
}

/**
 * Validate a date field.
 * @this ValidatorElement
 * @param getFn - If true, returns the compare function
 * @returns true if valid, or compare function if getFn is true
 */
function validateDate(this: ValidatorElement, getFn?: boolean): boolean | ((value: any) => number | null) {
  if (getFn) {
    this.converter = dateStringToInt;
    return compareConvertedFEW;
  }
  const date = (new MyDate()).setFromString(this.get(), this.partition);
  const mssg = date.checkIsValid();
  if (mssg != "") {
    return this.setMessage(mssg);
  } else {
    this.set(date.getAsString(this.monthFormat, this.yearFormat));
  }

  const overMax = this.compareNullable(this.maximum, ">");
  if (this.compareNullable(this.minimum, "<"))
    return this.setMessage("The date must not be before " + dateIntToString(getValueFor(this.minimum, dateStringToInt), this.monthFormat, this.yearFormat) + ".");
  if (overMax)
    return this.setMessage("The date must not be after " + dateIntToString(getValueFor(this.maximum, dateStringToInt), this.monthFormat, this.yearFormat) + ".");
  return true;
}

/**
 * Convert a date string to integer.
 * @param value - Date string
 * @returns Integer representation
 */
function dateStringToInt(value: string): number {
  return (new MyDate()).setFromString(value).getAsInt();
}

/**
 * Convert a date integer to string.
 * @param value - Integer date
 * @param monthFormat - Month format option
 * @param yearFormat - Year format option
 * @returns Date string
 */
function dateIntToString(value: number, monthFormat?: number, yearFormat?: number): string {
  return (new MyDate()).setFromInt(value).getAsString(monthFormat, yearFormat);
}

/**
 * Validate a time field.
 * @this ValidatorElement
 * @param getFn - If true, returns the compare function
 * @returns true if valid, or compare function if getFn is true
 */
function validateTime(this: ValidatorElement, getFn?: boolean): boolean | ((value: any) => number | null) {
  if (getFn) {
    this.converter = timeStringToInt;
    return compareConvertedFEW;
  }
  const time = (new MyTime()).setFromString(this.get());
  if (time.hour != null)
    this.set(time.getAsString(this.seconds == true, this.military == true));
  const mssg = time.checkIsValid();
  if (mssg != "") return this.setMessage(mssg);

  const minAsInt = getValueFor(this.minimum, timeStringToInt);
  const maxAsInt = getValueFor(this.maximum, timeStringToInt);
  const minAsStr = minAsInt != null ? timeIntToString(minAsInt, this.seconds == true, this.military == true) : "";
  const maxAsStr = maxAsInt != null ? timeIntToString(maxAsInt, this.seconds == true, this.military == true) : "";
  const isAfterMax = this.compareNullable(this.maximum, ">");
  const isBeforeMin = this.compareNullable(this.minimum, "<");
  if (maxAsInt == null || minAsInt == null) {
    if (isBeforeMin)
      return this.setMessage("The time must not be before " + minAsStr + ".");
    if (isAfterMax)
      return this.setMessage("The time must not be after " + maxAsStr + ".");
    return true;
  }
  if (minAsInt > maxAsInt) {
    if (!isAfterMax || !isBeforeMin)
      return true;
  } else {
    if (!isAfterMax && !isBeforeMin)
      return true;
  }
  return this.setMessage("The time must be between " + minAsStr + " and " + maxAsStr + ".");
}

/**
 * Convert a time string to integer.
 * @param value - Time string
 * @returns Integer representation
 */
function timeStringToInt(value: string): number {
  return (new MyTime()).setFromString(value).getAsInt();
}

/**
 * Convert a time integer to string.
 * @param value - Integer time
 * @param showSeconds - Whether to show seconds
 * @param military - Whether to use 24-hour format
 * @returns Time string
 */
function timeIntToString(value: number, showSeconds?: boolean, military?: boolean): string {
  return (new MyTime()).setFromInt(value).getAsString(showSeconds, military);
}

/**
 * Validate a month/year field.
 * @this ValidatorElement
 * @param getFn - If true, returns the compare function
 * @returns true if valid, or compare function if getFn is true
 */
function validateMonthYear(this: ValidatorElement, getFn?: boolean): boolean | ((value: any) => number | null) {
  if (getFn) {
    this.converter = monthYearStringToInt;
    return compareConvertedFEW;
  }
  if (this.minimum == "creditCardExpire") {
    const today = (new MyDate()).setFromDate(currentDateTime());
    this.minimum = today.month + "/" + today.year;
  }
  const month = new (MonthYear as any)(this.get(), this.partition);
  let mssg = month.checkIsValid();
  if (mssg != "") {
    if (mssg == "Please enter a valid date.") mssg = "Please enter a valid month and year.";
    return this.setMessage(mssg);
  } else {
    this.set(month.getAsString(this.monthFormat, this.yearFormat));
  }

  const overMax = this.compareNullable(this.maximum, ">");
  if (this.compareNullable(this.minimum, "<"))
    return this.setMessage("The month and year must not be before " + monthYearIntToString(getValueFor(this.minimum, monthYearStringToInt), this.monthFormat, this.yearFormat) + ".");
  if (overMax)
    return this.setMessage("The month and year date must not be after " + monthYearIntToString(getValueFor(this.maximum, monthYearStringToInt), this.monthFormat, this.yearFormat) + ".");
  return true;
}

/**
 * Convert a month/year string to integer.
 * @param value - Month/year string
 * @returns Integer representation
 */
function monthYearStringToInt(value: string): number {
  return (new (MonthYear as any)(value)).getAsInt();
}

/**
 * Convert a month/year integer to string.
 * @param value - Integer value
 * @param monthFormat - Month format option
 * @param yearFormat - Year format option
 * @returns Month/year string
 */
function monthYearIntToString(value: number, monthFormat?: number, yearFormat?: number): string {
  return (new (MonthYear as any)()).setFromInt(value).getAsString(monthFormat, yearFormat);
}

/**
 * Validate a list of times.
 * @this ValidatorElement
 * @returns true if valid
 */
function validateTimeList(this: ValidatorElement): boolean {
  let newTimes = "";
  const tokenizer = new Tokenizer(this.get(), ",; ");
  for (let token = tokenizer.nextToken(); token != null; token = tokenizer.nextToken()) {
    if (token != "") {
      const time = (new MyTime()).setFromString(token);
      const mssg = time.checkIsValid();
      if (mssg != "") return this.setMessage(mssg + " (" + token + ")");
      token = time.getAsString(this.seconds == true, this.military == true);
      if (newTimes.length > 0) newTimes += ", ";
      newTimes += token;
    }
  }
  this.set(newTimes);
  if (newTimes == "") return this.setMessage("This is a required field.");
  return true;
}

//********** MonthYear class **********

/**
 * MonthYear class for handling month/year dates.
 */
function MonthYear(this: MonthYearInstance, str?: string | null, partition?: number): void {
  this.checkIsValid = checkIsValidMD;
  this.setFromString = setFromStringMY;
  this.setFromInt = setFromIntMD;
  this.setFromDate = setFromDateMD;
  this.getAsString = getAsStringMY;
  this.getAsInt = getAsIntMD;
  this.getAsDate = getAsDateMD;
  this.getMonthName = getMonthNameMD;
  this.getDaysInMonth = getDaysInMonthMD;
  this.month = 0;
  this.year = 0;
  this.day = 1;
  if (str != null) this.setFromString(str, partition);
}

/**
 * Set month/year from string.
 * @this MonthYearInstance
 * @param str - Input string
 * @param partition - Year partition for 2-digit years
 * @returns this for chaining
 */
function setFromStringMY(this: MonthYearInstance, str: string, partition?: number): MonthYearInstance {
  if (str == TIMESTAMP) {
    const date = currentDateTime();
    this.year = date.getFullYear();
    this.month = date.getMonth() + 1;
    return this;
  }
  const tokens: any[] = parseDateTime(str, dateSeparators);
  if (tokens.length != 2) {
    tokens[0] = parseMonthName(str) + 1;
    tokens[1] = (tokens[0] > 12) ? null : extract(str, NUMERIC);
    if (tokens[1] == "") tokens[1] = currentYear();
  }
  if (tokens[0].length == 4) {
    const temp = tokens[0];
    tokens[0] = tokens[1];
    tokens[1] = temp;
  }
  if (tokens[0] == 0) return this;

  this.month = parseInt(tokens[0], 10);
  this.year = (tokens[1] == null) ? null : parseInt(tokens[1], 10);
  if (this.year != null && this.year < 100) {
    if (partition == null) partition = (currentYear() + 50) % 100;
    this.year = 2000 + ((this.year >= partition) ? this.year - 100 : this.year);
  }
  return this;
}

/**
 * Get month/year as string.
 * @this MonthYearInstance
 * @param monthFormat - Month format option
 * @param yearFormat - Year format option
 * @returns Formatted string
 */
function getAsStringMY(this: MonthYearInstance, monthFormat?: number, yearFormat?: number): string {
  let out = "";
  let yearSpacer = " ";
  if (monthFormat == SHORT_MONTH) {
    out += this.getMonthName(true);
  } else if (monthFormat != undefined && monthFormat >= LONG_MONTH) {
    out += this.getMonthName(false);
  } else {
    out += twoDigit(this.month);
    yearSpacer = "/";
  }
  if (yearFormat == FULL_YEAR) {
    out += yearSpacer + this.year;
  } else if (yearFormat != NO_YEAR) {
    out += yearSpacer + twoDigit(this.year! % 100);
  }
  return out;
}

// Export for module usage
export {
  validateDate,
  dateStringToInt,
  dateIntToString,
  validateTime,
  timeStringToInt,
  timeIntToString,
  validateMonthYear,
  monthYearStringToInt,
  monthYearIntToString,
  validateTimeList,
  MonthYear,
  setFromStringMY,
  getAsStringMY
};

// Export types
export type { ValidatorElement, MonthYearInstance };

// Expose on globalThis for backwards compatibility
(globalThis as any).validateDate = validateDate;
(globalThis as any).dateStringToInt = dateStringToInt;
(globalThis as any).dateIntToString = dateIntToString;
(globalThis as any).validateTime = validateTime;
(globalThis as any).timeStringToInt = timeStringToInt;
(globalThis as any).timeIntToString = timeIntToString;
(globalThis as any).validateMonthYear = validateMonthYear;
(globalThis as any).monthYearStringToInt = monthYearStringToInt;
(globalThis as any).monthYearIntToString = monthYearIntToString;
(globalThis as any).validateTimeList = validateTimeList;
(globalThis as any).MonthYear = MonthYear;
(globalThis as any).setFromStringMY = setFromStringMY;
(globalThis as any).getAsStringMY = getAsStringMY;

// Script resource notification
if (typeof _SR_ !== "undefined" && _SR_ !== null) {
  _SR_.notify("validatedate.js");
}
